import * as React from 'react';
import { Avatar, Box, Typography, Container } from 'components';
import {
  CloseIcon,
  LogoutIcon,
  MainIcon,
  MenuIcon,
  NotificationIcon,
  VideoIcon,
  TrainingIcon,
  SettingIcon,
} from 'components/Icon';
import LogoSPIcon from 'components/Icon/LogoSPIcon';
import { forwardRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import RouteName from 'config/routes';
import {
  StyledLink,
  Nav,
  MobileFlex,
  MobileNav,
  MenuItem,
  Menu,
  StyledHeader,
  NewBadge,
  DesktopOnlyMenuItem,
  MobileDrawerCategories,
  DrawerCategoryRow,
  DrawerCategoryIconSlot,
  CornerNewBadge,
} from './style';
import { useDispatch, useSelector } from 'react-redux';
import userSelector from 'selectors/userSelector';
import { resetAuthAction } from 'reducers/authReducer';
import latestNotificationSelector from 'selectors/notificationSelector';
import { useUpdateStatusNotificationMutation } from 'services/auth';
import { useGetTabCompaniesQuery, useGetNewTrainingsInfoQuery } from 'services/video';
import { shouldShowNewBadge } from 'utils/common';
import { CategoryCard } from 'shared/components/CategoryCard/style';
import { CategoryNext } from 'shared/components/CategoryNext/style';

const Header = forwardRef(() => {
  const { t } = useTranslation();
  const location = useLocation();
  const [drawerShown, setDrawerShown] = useState(false);
  const user = useSelector(userSelector);
  const latestNotification = useSelector(latestNotificationSelector);
  const [checkDisplay, setCheckDisplay] = useState(user ? user.status_notification : false);
  const [updateStatusNotification] = useUpdateStatusNotificationMutation();
  const { data: tabList } = useGetTabCompaniesQuery();
  const { data: newTrainingsInfo } = useGetNewTrainingsInfoQuery();

  useEffect(() => {
    if (latestNotification) {
      setCheckDisplay(latestNotification ? latestNotification?.status_notification : false);
    } else {
      setCheckDisplay(false);
    }
  }, [latestNotification]);

  const handleClick = async () => {
    if (latestNotification && latestNotification.status_notification === true) {
      try {
        const response = await updateStatusNotification(latestNotification?.company_notification?.id);
        if (response.data.success) {
          setCheckDisplay(false);
          window.location.reload();
        } else {
          setCheckDisplay(true);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const openDrawer = () => {
    setDrawerShown((prevState) => !prevState);
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const logout = () => {
    dispatch(resetAuthAction());
    navigate(RouteName.login);
  };

  useEffect(() => {
    setDrawerShown(false);
  }, [location]);

  const mobileCategoryLink = (to, onClick, icon, label, showCornerNew) => (
    <DrawerCategoryRow>
      <Link
        to={to}
        onClick={onClick}
        style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      >
        <CategoryCard>
          {showCornerNew && <CornerNewBadge />}
          <DrawerCategoryIconSlot>{icon}</DrawerCategoryIconSlot>
          <Typography clamp={2} weight="600">
            {label}
          </Typography>
          <CategoryNext width="16" color="primary" />
        </CategoryCard>
      </Link>
    </DrawerCategoryRow>
  );

  return (
    <StyledHeader>
      <Box
        component={Container}
        size="largest"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        padding="0 24px"
      >
        <Box xs={{ display: 'none' }} md={{ display: 'flex' }}>
          <Link to={RouteName.mainMenu}>
            <LogoSPIcon width={260} height={40} />
          </Link>
        </Box>
        <MobileFlex>
          <Link to={RouteName.mainMenu}>
            <LogoSPIcon />
          </Link>
          <Box cursor="pointer" display="flex" alignItems="center">
            <MenuIcon color="black" onClick={openDrawer} />
          </Box>
        </MobileFlex>
        <Nav show={drawerShown}>
          <MobileFlex marginBottom={21}>
            <LogoSPIcon />
            <Box cursor="pointer" display="flex" alignItems="center">
              <CloseIcon color="black" onClick={openDrawer} />
            </Box>
          </MobileFlex>
          <Menu>
            {!user ? (
              <Box component={MenuItem} md={{ display: 'none' }} xs={{ borderBottom: 'none', paddingBottom: 12 }}>
                <StyledLink to={RouteName.login}>{t('common:link:login')}</StyledLink>
              </Box>
            ) : (
              <MenuItem>
                <Box xs={{ display: 'none' }} textAlign="center">
                  <Link to={RouteName.myPage}>
                    <Avatar size={20} src={user?.photo} alt="logo"></Avatar>
                    <Typography>{t('common:link:myPage')}</Typography>
                  </Link>
                </Box>
                <Box md={{ display: 'none' }} display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" marginRight={4}>
                    <Box display="flex" marginRight={15}>
                      <Avatar size={52} src={user?.photo} alt="logo"></Avatar>
                    </Box>
                    <Typography variant="description" clamp={2}>
                      {user?.name}
                    </Typography>
                  </Box>
                  <StyledLink to={RouteName.myPage}>{t('common:link:myPage')}</StyledLink>
                </Box>
              </MenuItem>
            )}

            {user && (
              <MobileDrawerCategories>
                {mobileCategoryLink(
                  RouteName.mainMenu,
                  undefined,
                  <MainIcon color="primary" />,
                  t('common:link:mainMenu'),
                  false
                )}
                {mobileCategoryLink(
                  RouteName.training,
                  undefined,
                  <TrainingIcon height={25} width={25} marginBottom={-2} />,
                  t('common:link:training'),
                  shouldShowNewBadge(newTrainingsInfo?.data?.latest_required_training_created_at, user)
                )}
                {!user?.company?.hidden_training_categories_screen &&
                  mobileCategoryLink(
                    RouteName.videoCategories,
                    undefined,
                    <VideoIcon size="small" color="primary" />,
                    t('common:link:video'),
                    shouldShowNewBadge(newTrainingsInfo?.data?.latest_training_created_at, user)
                  )}
                {mobileCategoryLink(
                  RouteName.notification,
                  handleClick,
                  <NotificationIcon size="small" color="primary" />,
                  <>
                    {t('common:link:notification')}
                    {checkDisplay && <span style={{ color: 'red' }}> {t('sidebar:labelNew')}</span>}
                  </>,
                  false
                )}
                {tabList?.tabs?.map((item, index) => (
                  <DrawerCategoryRow key={item.category_tab_id ?? index}>
                    <Link
                      to={RouteName.tabVideoCategories.replace(':tabId', item.category_tab_id)}
                      state={{
                        fromPathName: RouteName.tabVideoCategories.replace(':tabId', item.category_tab_id),
                        tabName: item.category_tab.name,
                      }}
                      style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                    >
                      <CategoryCard>
                        {shouldShowNewBadge(item?.latest_training_created_at, user) && <CornerNewBadge />}
                        <DrawerCategoryIconSlot>
                          <VideoIcon size="small" color="primary" />
                        </DrawerCategoryIconSlot>
                        <Typography clamp={2} weight="600">
                          {item.category_tab.name}
                        </Typography>
                        <CategoryNext width="16" color="primary" />
                      </CategoryCard>
                    </Link>
                  </DrawerCategoryRow>
                ))}
                <DrawerCategoryRow>
                  <Link
                    to={RouteName.settings}
                    style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                  >
                    <CategoryCard>
                      <DrawerCategoryIconSlot>
                        <SettingIcon size="small" color="primary" />
                      </DrawerCategoryIconSlot>
                      <Typography clamp={2} weight="600">
                        {t('common:link:settings')}
                      </Typography>
                      <CategoryNext width="16" color="primary" />
                    </CategoryCard>
                  </Link>
                </DrawerCategoryRow>
                <DrawerCategoryRow>
                  <Box onClick={logout} cursor="pointer" width="100%">
                    <CategoryCard>
                      <DrawerCategoryIconSlot>
                        <LogoutIcon size="small" color="primary" />
                      </DrawerCategoryIconSlot>
                      <Typography clamp={2} weight="600">
                        {t('common:link:logout')}
                      </Typography>
                      <CategoryNext width="16" color="primary" />
                    </CategoryCard>
                  </Box>
                </DrawerCategoryRow>
              </MobileDrawerCategories>
            )}

            <DesktopOnlyMenuItem>
              <Link to={RouteName.training}>
                <TrainingIcon height={25} width={25} marginBottom={-2} />
                <Typography>{t('common:link:training')}</Typography>
              </Link>
            </DesktopOnlyMenuItem>
            {!user?.company?.hidden_training_categories_screen && (
              <DesktopOnlyMenuItem>
                <Link to={RouteName.videoCategories}>
                  <VideoIcon size="small" color="primary" />
                  <Typography>{t('common:link:video')}</Typography>
                </Link>
              </DesktopOnlyMenuItem>
            )}
            {user && (
              <DesktopOnlyMenuItem>
                <Link to={RouteName.notification} onClick={handleClick}>
                  <NotificationIcon size="small" color="primary" />
                  <Typography>{t('common:link:notification')}</Typography>
                  {checkDisplay && <NewBadge>NEW</NewBadge>}
                </Link>
              </DesktopOnlyMenuItem>
            )}
            {user && (
              <DesktopOnlyMenuItem>
                <Link to={RouteName.settings}>
                  <SettingIcon size="small" color="primary" />
                  <Typography>{t('common:link:settings')}</Typography>
                </Link>
              </DesktopOnlyMenuItem>
            )}
            {user && (
              <DesktopOnlyMenuItem>
                <Box component="a" onClick={logout}>
                  <LogoutIcon size="small" color="primary" />
                  <Typography>{t('common:link:logout')}</Typography>
                </Box>
              </DesktopOnlyMenuItem>
            )}
          </Menu>
        </Nav>
        {drawerShown && <MobileNav onClick={() => setDrawerShown(false)} />}
      </Box>
    </StyledHeader>
  );
});

export default Header;
